import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { CustomerRecordDto } from '../../dtos/customer-record.dto';
import { CustomerActionEnum } from '../../enums/customer-action.enum';
import { CustomerStatusEnum } from '../../enums/customer-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: CustomerRecordDto,
	action: CustomerActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	const fixedStatuses = [
		CustomerStatusEnum.ACTIVE,
		CustomerStatusEnum.CLOSED,
		CustomerStatusEnum.ON_HOLD,
	];

	switch (action) {
		case CustomerActionEnum.ACTIVATE:
			validationResult = setStatusSchema(
				CustomerStatusEnum.ACTIVE,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case CustomerActionEnum.CLOSE:
			validationResult = setStatusSchema(
				CustomerStatusEnum.CLOSED,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case CustomerActionEnum.PUT_ON_HOLD:
			validationResult = setStatusSchema(
				CustomerStatusEnum.ON_HOLD,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
